import React from 'react';
// Import Button component for styling consistency
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
// Updated icons for identity features
import { UserCheck, ShieldCheck, EyeOff, UserX, CheckCircle, FileCode, ArrowRight } from 'lucide-react'; 

const IdentityVerificationServices: React.FC = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 md:px-8 lg:px-10 max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row items-center gap-12">
          <div className="lg:w-1/2">
            <motion.h1 
              className="text-4xl md:text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-primary to-blue-500"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Identity Verification Services
            </motion.h1>
            <motion.p 
              className="text-xl md:text-2xl font-medium mb-8 text-foreground/80"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              Issuing trusted certificates for increased confidence and fraud prevention with every interaction.
            </motion.p>
            {/* Combined CTA Buttons Container - Using Button component styles */}
            <motion.div
              className="flex flex-col md:flex-row flex-wrap items-center gap-4 mt-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              {/* Documentation Button - Styled like Button */}
              <a
                href="https://docs.projectbabbage.com/docs/concepts/trust"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Learn more about Trust concepts in the documentation"
                className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-11 px-8 gap-2"
                role="button"
              >
                Learn More
                <ArrowRight className="w-4 h-4" />
              </a>
              {/* Contact CTA Button - Styled like Button */} 
              <a
                href="/Contact"
                aria-label="Contact us to get started"
                className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-11 px-8 gap-2"
                role="button"
              >
                Contact Us
                <ArrowRight className="w-4 h-4" />
              </a>
            </motion.div>
          </div>
          <motion.div 
            className="lg:w-1/2 flex items-center justify-center"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <UserCheck className="h-48 w-48 text-primary/50" />
          </motion.div>
        </div>
      </section>

      {/* Core Benefits Section - Styled like OverlayHostingServices */}
      <section className="py-20 bg-muted/50">
        <div className="px-4 sm:px-6 md:px-8 lg:px-10 max-w-7xl mx-auto">
          {/* Adjusted title centering and description */} 
          <div className="text-center mb-16">
            <motion.h2 
              className="text-3xl md:text-4xl font-bold mb-4 text-primary"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              Core Benefits
            </motion.h2>
             <motion.p 
               className="text-muted-foreground max-w-2xl mx-auto"
               initial={{ opacity: 0, y: 20 }}
               whileInView={{ opacity: 1, y: 0 }}
               viewport={{ once: true }}
               transition={{ duration: 0.5, delay: 0.1 }}
             > 
               How Babbage enhances trust and security through identity verification.
             </motion.p>
          </div>
          
          {/* Switched to grid layout with cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <ShieldCheck className="h-8 w-8 text-primary" />,
                title: "Reduced Fraud",
                description: "Tied to real cryptographic signatures, each claim (e.g., name, address) is verifiably authentic."
              },
              {
                icon: <EyeOff className="h-8 w-8 text-primary" />,
                title: "Granular Disclosure",
                description: "Users decrypt only specific fields—no forced over-sharing of sensitive data."
              },
              {
                icon: <UserX className="h-8 w-8 text-primary" />,
                title: "Revocation-Based Trust",
                description: "Certificates can indicate a \"revocation outpoint,\" so any service checking a revoked certificate sees it instantly as invalid."
              },
              {
                icon: <CheckCircle className="h-8 w-8 text-primary" />,
                title: "User-Defined Trust",
                description: "Each wallet chooses which certifiers to accept, ensuring no single authority monopolizes identity."
              },
              {
                icon: <FileCode className="h-8 w-8 text-primary" />,
                title: "Open-Standard Implementation",
                description: "Identity protocols integrate smoothly with other Babbage features like basket-managed UTXOs or data storage, letting apps request identity proofs without custom or proprietary methods."
              }
            ].map((feature, index) => (
              <motion.div
                key={feature.title}
                className="bg-card border rounded-xl p-8 shadow-sm hover:shadow-md transition-all duration-300"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="mb-4 bg-primary/10 w-14 h-14 rounded-lg flex items-center justify-center">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold mb-2 text-foreground">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </motion.div>
            ))}
          </div>
          
          {/* Concluding Paragraph */}
          <motion.div
            className="mt-24 text-center"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
             <p className="text-lg text-foreground/90">
               Through on-chain verification of user attributes, Project Babbage cuts down on fraudulent claims while ensuring minimal, user-controlled data sharing in every interaction.
             </p>
          </motion.div>

        </div>
      </section>
    </div>
  );
};

export default IdentityVerificationServices; 